import React, { createContext, useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";

import useComponentIsMounted from "Utilities/Hooks/useComponentIsMounted";

export const ResizingContainerContext = createContext();

export function SharedResizingContainer({
  children,
  direction = "left",
  bounce = false,
  forceWidthWrapper,
  onAnimationStart,
  onAnimationComplete,
  ...props
}) {
  const wrapperRef = useRef();
  const containerRef = useRef();

  const [isResizing, setIsResizing] = useState(false);

  const [newMeasurements, setNewMeasurements] = useState();
  const [measurements, setMeasurements] = useState();

  const [hasNewChild, setHasNewChild] = useState(false);

  const [isFirstMount, setIsFirstMount] = useState(true);

  const [previousMeasurements, setPreviousMeasurements] = useState(false);

  let childKey;
  const childrenArray = React.Children.toArray(children);
  childrenArray.forEach((child) => (childKey = child.key));

  const previousChildKey = useRef(childKey);

  const animationStart = () => {
    if (onAnimationStart) onAnimationStart();
  };
  const animationComplete = (e) => {
    cleanupAfterAnimation();

    if (onAnimationComplete) onAnimationComplete();
  };

  const cleanupAfterAnimation = () => {
    setIsResizing(false);
    setHasNewChild(false);
    setIsFirstMount(false);
    setPreviousMeasurements(false);
  };

  const parentIsMounted = useComponentIsMounted();

  const context = {
    setNewMeasurements,
    direction,
    bounce,
    parentIsMounted,
    isResizing,
    forceWidthWrapper,
  };

  useEffect(() => {
    setIsResizing(true);
  }, [newMeasurements?.height, newMeasurements?.width]);

  useEffect(() => {
    if (isResizing) {
      setMeasurements(newMeasurements);

      if (isFirstMount) cleanupAfterAnimation();
    }
  }, [isResizing]);

  const hasNewChild_Immediate = childKey !== previousChildKey.current;

  useEffect(() => {
    if (hasNewChild_Immediate) {
      setHasNewChild(true);
      previousChildKey.current = childKey;
      setPreviousMeasurements(wrapperRef.current.getBoundingClientRect());
    }
  }, [hasNewChild_Immediate]);

  return (
    <Wrapper
      {...props}
      className={classNames({
        isResizing: isResizing || hasNewChild || hasNewChild_Immediate,
      })}
      ref={wrapperRef}
    >
      {measurements && isResizing && (
        <ResizingSpace
          ref={containerRef}
          onAnimationStart={animationStart}
          onAnimationComplete={animationComplete}
          initial={
            previousMeasurements && {
              width: previousMeasurements.width,
              height: previousMeasurements.height,
            }
          }
          animate={{
            width: measurements.width,
            height: measurements.height,
            transition: bounce
              ? {}
              : { type: "tween", duration: 0.3, ease: "easeOut" },
          }}
        />
      )}
      <ResizingContainerContext.Provider value={context}>
        <AnimatePresence>{children}</AnimatePresence>
      </ResizingContainerContext.Provider>
    </Wrapper>
  );
}

const Wrapper = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  &.isResizing {
    overflow: hidden !important;
  }
`;

const ResizingSpace = styled(motion.div)`
  display: none;
  z-index: -1;

  // prettier-ignore
  ${Wrapper}.isResizing & {
    display: block;
  }
`;
